import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { X, Check } from 'lucide-react';

const CgvGateContext = createContext(null);
const STORAGE_KEY = 'harmony_cgv_accepted_v1';

export const useCgvGate = () => {
  const ctx = useContext(CgvGateContext);
  if (!ctx) throw new Error('useCgvGate must be used within <CgvGateProvider>');
  return ctx;
};

export const CgvGateProvider = ({ children }) => {
  const [pendingAction, setPendingAction] = useState(null); // { run: fn, label: string }
  const [checked, setChecked] = useState(false);

  // requireAcceptance(label, fn): si déjà accepté → run direct, sinon ouvre la modale
  const requireAcceptance = useCallback((label, fn) => {
    try {
      if (sessionStorage.getItem(STORAGE_KEY) === '1') {
        fn();
        return;
      }
    } catch (e) {
      console.warn('CgvGate: sessionStorage read failed', e);
    }
    setChecked(false);
    setPendingAction({ run: fn, label });
  }, []);

  const handleConfirm = () => {
    if (!checked || !pendingAction) return;
    try {
      sessionStorage.setItem(STORAGE_KEY, '1');
    } catch (e) {
      console.warn('CgvGate: sessionStorage write failed', e);
    }
    const { run } = pendingAction;
    setPendingAction(null);
    // Run after closing modal so window.open is still considered user-initiated
    run();
  };

  const handleClose = () => setPendingAction(null);

  const value = useMemo(() => ({ requireAcceptance }), [requireAcceptance]);

  return (
    <CgvGateContext.Provider value={value}>
      {children}
      {pendingAction && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-[#332724]/70 backdrop-blur-sm animate-in fade-in"
          onClick={handleClose}
          data-testid="cgv-gate-modal"
        >
          <div
            className="bg-[#FDFAF4] rounded-2xl max-w-lg w-full p-7 shadow-[0_30px_80px_rgba(92, 32, 24,0.35)] relative border border-[#B8922A]/30"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 text-[#332724]/50 hover:text-[#5C2018] transition-colors"
              aria-label="Fermer"
              data-testid="cgv-gate-close"
            >
              <X size={20} />
            </button>

            <p className="text-xs uppercase tracking-[0.3em] text-[#B8922A] mb-3">
              Avant de finaliser
            </p>
            <h3
              className="text-3xl mb-4 text-[#5C2018]"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
            >
              Confirmation de commande
            </h3>
            <p className="text-sm text-[#332724] font-light leading-relaxed mb-2">
              Vous vous apprêtez à acheter&nbsp;:
            </p>
            <p className="text-sm font-medium text-[#5C2018] mb-5">
              {pendingAction.label}
            </p>

            <label className="flex items-start gap-3 cursor-pointer mb-4 p-3 rounded-lg bg-white border border-[#B8922A]/30 hover:border-[#1E7A6A] transition-colors">
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
                className="mt-1 w-4 h-4 accent-[#1E7A6A]"
                data-testid="cgv-gate-checkbox"
              />
              <span className="text-sm text-[#332724] font-light leading-relaxed">
                J&apos;ai lu et j&apos;accepte les{' '}
                <Link
                  to="/cgv"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#5C2018] underline hover:text-[#B8922A]"
                  data-testid="cgv-gate-link"
                >
                  Conditions Générales de Vente
                </Link>{' '}
                d&apos;Harmony Parfumerie, incluant les modalités de
                <strong> Click &amp; Collect</strong> (retrait obligatoire en boutique, sans
                remboursement en cas de non-retrait) et de
                <strong> livraison Colissimo</strong> (Harmony Parfumerie ne peut être tenue
                responsable du transport effectué par La Poste).
              </span>
            </label>

            <button
              type="button"
              onClick={handleConfirm}
              disabled={!checked}
              className={`w-full px-6 py-3 rounded-full text-xs uppercase tracking-[0.2em] flex items-center justify-center gap-2 transition-all ${
                checked
                  ? 'bg-[#5C2018] text-[#FDFAF4] hover:bg-[#4A1810] shadow-[0_10px_30px_rgba(92, 32, 24,0.25)]'
                  : 'bg-[#B8922A]/30 text-[#332724]/40 cursor-not-allowed'
              }`}
              data-testid="cgv-gate-confirm"
            >
              <Check size={14} />
              Accepter et continuer vers le paiement
            </button>
            <p className="text-[10px] text-center text-[#332724]/50 mt-3 italic">
              Vous serez redirigé(e) vers la plateforme de paiement sécurisée.
            </p>
          </div>
        </div>
      )}
    </CgvGateContext.Provider>
  );
};

export default CgvGateProvider;
